import { useState, useEffect } from 'react';
import { useTheme } from './theme';
import { db } from './dbClient';

export default function TeamPerformance({ company, team = [] }) {
  const C = useTheme();
  const [teamPerformance, setTeamPerformance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (company?.id) {
      loadTeamPerformance();
    }
  }, [company?.id]);

  async function loadTeamPerformance() {
    if (!company?.id) return;
    try {
      setLoading(true);

      // Load all cases to count completed cases per team member
      const { data: casesData, error: casesErr } = await db
        .from('cases')
        .select('*')
        .eq('company_id', company.id);

      if (casesErr) throw casesErr;

      // Load QA queue to count assigned cases and overdue items
      const { data: queueData, error: queueErr } = await db
        .from('qa_queue')
        .select('*')
        .eq('company_id', company.id);

      if (queueErr) throw queueErr;

      // Load QA reviews for scoring
      const { data: reviewData, error: reviewErr } = await db
        .from('qa_reviews')
        .select('*')
        .eq('company_id', company.id);

      if (reviewErr) throw reviewErr;

      // Build performance metrics by team member
      const performance = team.map(member => {
        const completedCases = casesData?.filter(c => c.assigned_to === member.id && c.status === 'closed').length || 0;
        const assignedQACases = queueData?.filter(q => q.assigned_to === member.id).length || 0;
        const overdueCases = queueData?.filter(q =>
          q.assigned_to === member.id && q.due_date && new Date(q.due_date) < new Date() && q.status !== 'completed'
        ).length || 0;

        const memberReviews = reviewData?.filter(r => {
          const qa = queueData?.find(q => q.id === r.qa_queue_id);
          return qa?.assigned_to === member.id;
        }) || [];
        const avgScore = memberReviews.length ? Math.round(
          memberReviews.reduce((sum, r) => sum + (r.analyst_score || 0), 0) / memberReviews.length
        ) : 0;

        return {
          id: member.id,
          name: member.name || member.email,
          email: member.email,
          completedCases,
          assignedCases: assignedQACases,
          overdueCases,
          avgScore,
          reviewCount: memberReviews.length
        };
      });

      setTeamPerformance(performance);
    } catch (err) {
      console.error('Error loading team performance:', err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <div style={{ padding: '2rem', color: C.textDim }}>Loading team performance...</div>;
  }

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ color: C.text, marginBottom: '0.5rem', fontFamily: "'Syne', sans-serif" }}>
          Team Performance
        </h1>
        <p style={{ color: C.textDim, fontSize: '0.9rem' }}>
          Individual metrics and performance tracking for your team.
        </p>
      </div>

      {teamPerformance && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '1.5rem', overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.9rem'
          }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                <th style={{ padding: '0.75rem', textAlign: 'left', color: C.textDim, fontWeight: 600, fontSize: '0.75rem' }}>TEAM MEMBER</th>
                <th style={{ padding: '0.75rem', textAlign: 'center', color: C.textDim, fontWeight: 600, fontSize: '0.75rem' }}>CASES COMPLETED</th>
                <th style={{ padding: '0.75rem', textAlign: 'center', color: C.textDim, fontWeight: 600, fontSize: '0.75rem' }}>CASES ASSIGNED</th>
                <th style={{ padding: '0.75rem', textAlign: 'center', color: C.textDim, fontWeight: 600, fontSize: '0.75rem' }}>OVERDUE</th>
                <th style={{ padding: '0.75rem', textAlign: 'center', color: C.textDim, fontWeight: 600, fontSize: '0.75rem' }}>QA REVIEWS</th>
                <th style={{ padding: '0.75rem', textAlign: 'center', color: C.textDim, fontWeight: 600, fontSize: '0.75rem' }}>AVG SCORE</th>
              </tr>
            </thead>
            <tbody>
              {teamPerformance.map(member => (
                <tr key={member.id} style={{ borderBottom: `1px solid ${C.border}`, background: C.bg }}>
                  <td style={{ padding: '1rem 0.75rem', color: C.text, fontWeight: 500 }}>
                    {member.name}
                  </td>
                  <td style={{ padding: '1rem 0.75rem', textAlign: 'center', color: C.text, fontWeight: 600 }}>
                    {member.completedCases}
                  </td>
                  <td style={{ padding: '1rem 0.75rem', textAlign: 'center', color: C.text, fontWeight: 600 }}>
                    {member.assignedCases}
                  </td>
                  <td style={{
                    padding: '1rem 0.75rem',
                    textAlign: 'center',
                    color: member.overdueCases > 0 ? '#FF6B6B' : C.text,
                    fontWeight: member.overdueCases > 0 ? 700 : 600
                  }}>
                    {member.overdueCases > 0 ? `⚠️ ${member.overdueCases}` : '—'}
                  </td>
                  <td style={{ padding: '1rem 0.75rem', textAlign: 'center', color: C.text, fontWeight: 600 }}>
                    {member.reviewCount}
                  </td>
                  <td style={{ padding: '1rem 0.75rem', textAlign: 'center' }}>
                    <div style={{
                      display: 'inline-block',
                      padding: '0.4rem 0.8rem',
                      background: member.avgScore >= 85 ? '#4CAF5022' : member.avgScore >= 70 ? '#FFA50022' : '#FF6B6B22',
                      color: member.avgScore >= 85 ? '#4CAF50' : member.avgScore >= 70 ? '#FFA500' : '#FF6B6B',
                      borderRadius: 4,
                      fontWeight: 700,
                      fontSize: '0.85rem'
                    }}>
                      {member.avgScore > 0 ? `${member.avgScore}%` : '—'}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
